import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail, Bell, Heart, MessageCircle, Users, Shield, CreditCard, Sparkles, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface EmailPreferences {
  email_pref_likes: boolean;
  email_pref_messages: boolean;
  email_pref_matches: boolean;
  email_pref_digest: boolean;
  email_pref_marketing: boolean;
  email_pref_verification: boolean;
  email_pref_subscription: boolean;
  unsubscribed_from_emails: boolean;
}

const EmailPreferences = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          email_pref_likes,
          email_pref_messages,
          email_pref_matches,
          email_pref_digest,
          email_pref_marketing,
          email_pref_verification,
          email_pref_subscription,
          unsubscribed_from_emails
        `)
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setPreferences(data as EmailPreferences);
    } catch (error) {
      debugLogger.error('EmailPreferences', 'Error fetching preferences:', error);
      toast.error('Erro ao carregar preferências');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key: keyof EmailPreferences, value: boolean) => {
    if (!user?.id || !preferences) return;

    // If turning off all emails, update the master switch
    if (key === 'unsubscribed_from_emails' && value) {
      // Unsubscribing from all
      setSaving(true);
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ unsubscribed_from_emails: true })
          .eq('id', user.id);

        if (error) throw error;
        setPreferences({ ...preferences, unsubscribed_from_emails: true });
        toast.success('Você foi descadastrado de todos os emails');
      } catch (error) {
        debugLogger.error('EmailPreferences', 'Error updating preference:', error);
        toast.error('Erro ao atualizar preferência');
      } finally {
        setSaving(false);
      }
      return;
    }

    // If re-subscribing, also set master switch to false
    const updateData: Partial<EmailPreferences> = { [key]: value };
    if (preferences.unsubscribed_from_emails && value) {
      updateData.unsubscribed_from_emails = false;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) throw error;
      setPreferences({ ...preferences, ...updateData });
      toast.success('Preferência atualizada');
    } catch (error) {
      debugLogger.error('EmailPreferences', 'Error updating preference:', error);
      toast.error('Erro ao atualizar preferência');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-luxury-pinkBright" />
      </div>
    );
  }

  const PreferenceRow = ({
    icon: Icon,
    title,
    description,
    preferenceKey,
    disabled = false,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
    preferenceKey: keyof EmailPreferences;
    disabled?: boolean;
  }) => (
    <div className="flex items-start justify-between py-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-white/5 rounded-lg">
          <Icon className="h-5 w-5 text-luxury-pinkBright" />
        </div>
        <div>
          <p className="text-white font-medium">{title}</p>
          <p className="text-sm text-white/60">{description}</p>
        </div>
      </div>
      <Switch
        checked={preferences?.[preferenceKey] ?? true}
        onCheckedChange={(value) => updatePreference(preferenceKey, value)}
        disabled={disabled || saving || preferences?.unsubscribed_from_emails}
        className="data-[state=checked]:bg-luxury-pinkBright"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center gap-3 px-4 py-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-white">Preferências de Email</h1>
            <p className="text-sm text-white/60">Gerencie seus emails</p>
          </div>
        </div>
      </div>

      <div className="p-4 pb-24 md:pb-6 space-y-4 max-w-2xl mx-auto">
        {/* Master Switch */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/10 rounded-lg">
                  <Mail className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-base">Descadastrar de todos</CardTitle>
                  <CardDescription className="text-white/60">
                    Desative todos os emails de uma vez
                  </CardDescription>
                </div>
              </div>
              <Switch
                checked={preferences?.unsubscribed_from_emails ?? false}
                onCheckedChange={(value) => updatePreference('unsubscribed_from_emails', value)}
                disabled={saving}
                className="data-[state=checked]:bg-red-500"
              />
            </div>
          </CardHeader>
        </Card>

        {preferences?.unsubscribed_from_emails && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-amber-200 text-sm">
            <p className="font-medium">Você está descadastrado de todos os emails</p>
            <p className="text-amber-200/80 mt-1">
              Ative qualquer categoria abaixo para voltar a receber emails.
            </p>
          </div>
        )}

        {/* Engagement Emails */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Heart className="h-4 w-4 text-luxury-pinkBright" />
              Interações
            </CardTitle>
            <CardDescription className="text-white/60">
              Notificações sobre curtidas, matches e mensagens
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-0 divide-y divide-white/10">
            <PreferenceRow
              icon={Heart}
              title="Novas curtidas"
              description="Quando alguém curtir seu perfil"
              preferenceKey="email_pref_likes"
            />
            <PreferenceRow
              icon={Users}
              title="Novos matches"
              description="Quando vocês curtirem um ao outro"
              preferenceKey="email_pref_matches"
            />
            <PreferenceRow
              icon={MessageCircle}
              title="Novas mensagens"
              description="Quando receber mensagens não lidas"
              preferenceKey="email_pref_messages"
            />
          </CardContent>
        </Card>

        {/* Digest */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Bell className="h-4 w-4 text-luxury-pinkBright" />
              Resumo Diário
            </CardTitle>
            <CardDescription className="text-white/60">
              Um resumo da sua atividade diária
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PreferenceRow
              icon={Sparkles}
              title="Digest diário"
              description="Resumo de curtidas, views e mensagens do dia"
              preferenceKey="email_pref_digest"
            />
          </CardContent>
        </Card>

        {/* Transactional */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-luxury-pinkBright" />
              Transacionais
            </CardTitle>
            <CardDescription className="text-white/60">
              Atualizações importantes sobre sua conta
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-0 divide-y divide-white/10">
            <PreferenceRow
              icon={Shield}
              title="Verificação"
              description="Atualizações sobre verificação de conta"
              preferenceKey="email_pref_verification"
            />
            <PreferenceRow
              icon={CreditCard}
              title="Assinatura"
              description="Alertas de expiração e renovação"
              preferenceKey="email_pref_subscription"
            />
          </CardContent>
        </Card>

        {/* Marketing */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-luxury-pinkBright" />
              Marketing
            </CardTitle>
            <CardDescription className="text-white/60">
              Novidades, promoções e dicas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PreferenceRow
              icon={Sparkles}
              title="Emails promocionais"
              description="Ofertas especiais e novidades"
              preferenceKey="email_pref_marketing"
            />
          </CardContent>
        </Card>

        <p className="text-xs text-white/40 text-center px-4">
          Emails de segurança (login, recuperação de senha) serão sempre enviados.
        </p>
      </div>
    </div>
  );
};

export default EmailPreferences;
